import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../api";

const QUERY_KEYS = {
  notifications: "marketingNotifications",
  summary: "marketingNotificationsSummary",
};

export function useMarketingNotifications(
  userId: string,
  filters?: Record<string, unknown>,
) {
  return useQuery({
    queryKey: [QUERY_KEYS.notifications, userId, filters],
    queryFn: () => api.marketing.notifications.list(userId, filters),
    enabled: !!userId,
    staleTime: 30 * 1000,
  });
}

export function useMarketingNotificationSummary(userId: string) {
  return useQuery({
    queryKey: [QUERY_KEYS.summary, userId],
    queryFn: () => api.marketing.notifications.summary(userId),
    enabled: !!userId,
    refetchInterval: 60 * 1000,
    staleTime: 30 * 1000,
  });
}

export function useMarkMarketingNotificationRead(userId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.marketing.notifications.markRead(userId, id),
    onMutate: async (id: string) => {
      await queryClient.cancelQueries({ queryKey: [QUERY_KEYS.notifications] });
      await queryClient.cancelQueries({ queryKey: [QUERY_KEYS.summary] });

      const previousNotifications = queryClient.getQueriesData({
        queryKey: [QUERY_KEYS.notifications],
      });
      const previousSummary = queryClient.getQueryData([
        QUERY_KEYS.summary,
        userId,
      ]);

      queryClient.setQueriesData<Record<string, unknown>>(
        { queryKey: [QUERY_KEYS.notifications] },
        (old) => {
          if (!old?.data) return old;
          const data = old.data as Array<{ id: string; isRead: boolean }>;
          return {
            ...old,
            data: data.map((n) =>
              n.id === id ? { ...n, isRead: true } : n,
            ),
          };
        },
      );

      queryClient.setQueryData<Record<string, unknown>>(
        [QUERY_KEYS.summary, userId],
        (old) => {
          if (!old) return old;
          return { ...old, unread: Math.max(0, ((old.unread as number) ?? 1) - 1) };
        },
      );

      return { previousNotifications, previousSummary };
    },
    onError: (_err, _id, context) => {
      if (context?.previousNotifications) {
        for (const [key, data] of context.previousNotifications) {
          queryClient.setQueryData(key, data);
        }
      }
      if (context?.previousSummary) {
        queryClient.setQueryData(
          [QUERY_KEYS.summary, userId],
          context.previousSummary,
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.summary] });
    },
  });
}

export function useMarkAllMarketingNotificationsRead(userId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.marketing.notifications.markAllRead(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.notifications],
      });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.summary] });
    },
  });
}
